import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/lib/lang";
import { Reveal } from "@/lib/reveal";
import { track } from "@/lib/analytics";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookLanding({ pageLang }) {
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState({ open: false, kind: "chapter" });
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (pageLang && pageLang !== lang) setLang(pageLang); }, [pageLang]); // eslint-disable-line
  const p = t.bookPage;
  useEffect(() => { document.title = p.metaTitle; track("view_book_page", { lang: pageLang }); }, [p.metaTitle, pageLang]);

  const isHe = lang === "he";
  const Arrow = isHe ? ChevronLeft : ChevronRight;
  const otherPath = pageLang === "en" ? "/he/book" : "/book";

  const openDialog = (kind) => { setForm({ name: "", email: "", note: "" }); setDialog({ open: true, kind }); };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error(p.errorMsg); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/book-lead`, { email: form.email, name: form.name, note: form.note, kind: dialog.kind, lang });
      track("book_lead", { kind: dialog.kind });
      if (dialog.kind === "chapter") track("download_chapter");
      toast.success(p.thanks);
      setDialog({ open: false, kind: dialog.kind });
    } catch (err) {
      toast.error(err?.response?.data?.detail || p.errorMsg);
    } finally { setLoading(false); }
  };

  const dialogMeta = {
    chapter: { title: p.leadTitle, body: p.leadBody, cta: p.leadCta, note: false },
    notify: { title: p.amazonNotifyCta, body: p.amazonFallback, cta: p.amazonNotifyCta, note: false },
    bulk: { title: p.bulkTitle, body: p.bulkBody, cta: p.bulkCta, note: true },
  }[dialog.kind];

  return (
    <div data-testid="book-landing">
      <Toaster position="top-center" theme="dark" richColors dir={isHe ? "rtl" : "ltr"} />

      {/* HEADER */}
      <header className="qs-nav book-header">
        <Link to="/" className="logo" data-testid="book-logo">{t.logo}</Link>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <Link to="/" className="txt book-back" data-testid="book-back-link" style={{ color: "#d8d2c9", fontSize: 15, fontWeight: 600, whiteSpace: "nowrap" }}>{p.backToSite}</Link>
          <button className="lang-toggle" data-testid="book-lang-toggle" onClick={() => navigate(otherPath)} aria-label="Switch language">
            <span className={lang === "en" ? "on" : ""}>EN</span><span className="sep">/</span><span className={lang === "he" ? "on" : ""}>עב</span>
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="pad two-col even section-line" style={{ paddingTop: 150 }} data-testid="book-hero">
        <div>
          <Reveal className="eyebrow">{p.eyebrow}</Reveal>
          <Reveal delay={0.05}><h1 className="big" data-testid="book-h1" style={{ fontSize: "clamp(34px,4.6vw,56px)" }}>{p.h1}</h1></Reveal>
          <Reveal delay={0.1}><p className="body" style={{ marginBottom: 36 }}>{p.sub}</p></Reveal>
          <Reveal delay={0.15}>
            <div className="cta-row">
              <a href="#buy" className="btn" data-testid="book-cta-primary" onClick={(e) => { e.preventDefault(); document.querySelector("#buy").scrollIntoView({ behavior: "smooth" }); }}>{p.ctaPrimary}</a>
              <button className="btn-outline" data-testid="book-cta-secondary" onClick={() => openDialog("chapter")}>{p.ctaSecondary}</button>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className={t.book.mockup ? "frame book-mockup" : "frame p23"} data-testid="book-hero-image">
          <img src={t.book.image} alt={t.book.title} />
        </Reveal>
      </section>

      {/* WHO LINE */}
      <div className="marquee" style={{ padding: "22px 24px", textAlign: "center" }}>
        <span className="marquee-item" style={{ fontSize: 22 }}>{p.whoLine}</span>
      </div>

      {/* VIDEO */}
      <section className="pad section-line" data-testid="book-video">
        <Reveal className="eyebrow">{p.videoTitle}</Reveal>
        <Reveal delay={0.05}>
          <div className="frame" style={{ aspectRatio: "16/9", maxWidth: 900, display: "flex", alignItems: "center", justifyContent: "center", background: "#2c2721" }}>
            <span className="body" style={{ margin: 0 }}>{p.videoNote}</span>
          </div>
        </Reveal>
      </section>

      {/* PROBLEMS */}
      <section className="pad section-line" data-testid="book-problems">
        <Reveal className="eyebrow">{p.problemsTitle}</Reveal>
        <div className="cards" style={{ marginTop: 40 }}>
          {p.problems.map((pr, i) => (
            <motion.div className="card" key={i} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay: i * 0.1 }}>
              <div className="cnum">{pr.n}</div><h3>{pr.t}</h3><p>{pr.b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FOR / NOT FOR */}
      <section className="pad alt section-line two-col even" data-testid="book-fit">
        <Reveal>
          <div className="eyebrow">{p.forTitle}</div>
          {p.forList.map((f, i) => (
            <div key={i} className="body" style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
              <Check size={20} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 4 }} /><span>{f}</span>
            </div>
          ))}
        </Reveal>
        <Reveal delay={0.08}>
          <div className="eyebrow">{p.notForTitle}</div>
          {p.notForList.map((f, i) => (
            <div key={i} className="body" style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
              <X size={20} style={{ color: "var(--text-3)", flexShrink: 0, marginTop: 4 }} /><span>{f}</span>
            </div>
          ))}
        </Reveal>
      </section>

      {/* REVIEWS */}
      <section className="pad section-line" data-testid="book-reviews">
        <Reveal className="eyebrow">{p.reviewsTitle}</Reveal>
        <Reveal delay={0.05}><h2 className="mid">{p.reviewsAgg}</h2></Reveal>
        <div className="two-col even" style={{ marginTop: 40, alignItems: "stretch" }}>
          {p.reviews.map((r, i) => (
            <Reveal key={i} delay={0.06 * i} className="card" style={{ border: "1px solid var(--line)" }}>
              <p className="body sm" style={{ margin: 0 }}>{r.text}</p>
              <div className="cnum" style={{ fontSize: 18, marginTop: 8 }}>— {r.name}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BUY */}
      <section className="pad alt section-line" id="buy" data-testid="book-buy">
        <Reveal className="eyebrow">{p.buyTitle}</Reveal>
        <Reveal delay={0.05}><p className="body" style={{ marginBottom: 40 }}>{p.buyNote}</p></Reveal>
        <div style={{ display: "flex", flexDirection: "column", gap: 1, background: "var(--line)", border: "1px solid var(--line)" }}>
          {p.editions.map((ed, i) => (
            <Reveal key={i} delay={0.04 * i} style={{ background: "var(--bg)", padding: "24px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
              <div>
                <div className="head" style={{ fontSize: 22, fontWeight: 700, color: "#f7f3ec" }}>{ed.label}</div>
                <div className="body sm" style={{ margin: "4px 0 0", color: "var(--text-3)" }}>{ed.meta}</div>
              </div>
              <Link to={`/go/${ed.store}`} target="_blank" rel="noopener noreferrer" className="btn" data-testid={`buy-${ed.store}`}>{ed.cta}</Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <button className="link-arrow" data-testid="amazon-notify-btn" style={{ background: "none", border: "none", cursor: "pointer", marginTop: 24 }} onClick={() => openDialog("notify")}>
            {p.amazonNotifyCta} <Arrow size={16} style={{ display: "inline", verticalAlign: "middle" }} />
          </button>
        </Reveal>
      </section>

      {/* CHAPTER LEAD MAGNET */}
      <section className="pad section-line two-col even" data-testid="book-chapter">
        <div>
          <Reveal className="eyebrow">{p.leadTitle}</Reveal>
          <Reveal delay={0.05}><p className="body" style={{ marginBottom: 28 }}>{p.leadBody}</p></Reveal>
          <Reveal delay={0.1}><button className="btn" data-testid="chapter-cta" onClick={() => openDialog("chapter")}>{p.leadCta}</button></Reveal>
        </div>
        <Reveal delay={0.08}>
          <div className="eyebrow">{p.bulkTitle}</div>
          <p className="body" style={{ marginBottom: 28 }}>{p.bulkBody}</p>
          <button className="btn-outline" data-testid="bulk-cta" onClick={() => openDialog("bulk")}>{p.bulkCta}</button>
        </Reveal>
      </section>

      {/* AUTHOR */}
      <section className="pad alt section-line two-col" data-testid="book-author">
        <Reveal className="frame p45"><img src={"https://customer-assets-7cd3h4nn.emergentagent.net/job_responsive-web-56/artifacts/3s6m33mw_2017-07-21%2023.29.18.webp"} alt={p.authorTitle} /></Reveal>
        <div>
          <Reveal className="eyebrow">{p.authorTitle}</Reveal>
          <Reveal delay={0.05}><h2 className="mid">בני מאיר / Benny Mayer</h2></Reveal>
          <Reveal delay={0.1}><p className="body">{p.authorBody}</p></Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="pad section-line" data-testid="book-faq">
        <Reveal className="eyebrow">{p.faqTitle}</Reveal>
        <Reveal delay={0.05}>
          <Accordion type="single" collapsible style={{ maxWidth: 820, marginTop: 32 }}>
            {p.faq.map((item, i) => (
              <AccordionItem value={`item-${i}`} key={i} style={{ borderBottom: "1px solid var(--line)" }}>
                <AccordionTrigger data-testid={`faq-${i}`} className="head" style={{ fontSize: 20, color: "#f4f0ea", textAlign: isHe ? "right" : "left" }}>{item.q}</AccordionTrigger>
                <AccordionContent className="body" style={{ fontSize: 16 }}>{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      {/* SECONDARY */}
      <section className="pad alt" data-testid="book-secondary">
        <Reveal className="eyebrow">{p.secondaryTitle}</Reveal>
        <Reveal delay={0.05}><p className="body" style={{ marginBottom: 28 }}>{p.secondaryBody}</p></Reveal>
        <Reveal delay={0.1}><Link to="/" className="link-arrow" data-testid="secondary-cta">{p.secondaryCta} <Arrow size={16} style={{ display: "inline", verticalAlign: "middle" }} /></Link></Reveal>
        <footer className="qs-footer" style={{ marginTop: 80 }}>
          <div className="logo">{t.contact.footerName}</div>
          <div className="copy">© {new Date().getFullYear()} {t.logo}. {t.contact.rights}</div>
        </footer>
      </section>

      {/* LEAD DIALOG */}
      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="lead-dialog" style={{ background: "var(--bg-alt)", border: "1px solid var(--line)", color: "var(--text)" }} dir={isHe ? "rtl" : "ltr"}>
          <DialogHeader>
            <DialogTitle className="head" style={{ color: "#f7f3ec", fontSize: 24, textAlign: isHe ? "right" : "left" }}>{dialogMeta.title}</DialogTitle>
            <DialogDescription className="body sm" style={{ textAlign: isHe ? "right" : "left" }}>{dialogMeta.body}</DialogDescription>
          </DialogHeader>
          <form className="qs-form" onSubmit={submitLead} style={{ maxWidth: "100%" }}>
            <input type="text" placeholder={p.namePlaceholder} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="lead-name" style={{ marginBottom: 14 }} />
            <input type="email" placeholder={p.emailPlaceholder} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="lead-email" required style={{ marginBottom: 14 }} />
            {dialogMeta.note && <textarea placeholder={p.notePlaceholder} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="lead-note" style={{ minHeight: 90, marginBottom: 14 }} />}
            <button type="submit" className="btn" disabled={loading} data-testid="lead-submit">{loading ? p.sending : dialogMeta.cta}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
